/**
 * Progression, XP, séries et activité — persistés dans la Realtime Database.
 *
 * Schéma (users/$uid) :
 *   profile/                 { displayName, year, dailyGoalMinutes, createdAt, notifications?, learningProfile? }
 *   stats/                   { xp, level, streak: {current,best,lastActiveDay}, totalExercises,
 *                              totalCorrect, totalTests, chaptersMastered }
 *   activity/$day            { xp, exercises, seconds }         (day = YYYY-MM-DD)
 *   progress/$chapterId      ChapterProgress (ci-dessous, dont `review` = révision espacée, Phase 2b)
 *   chats/$chapterId/messages/$pushId  { role, content, at }
 *   badges/$badgeId          { unlockedAt }
 *   parentAccess/            { currentCode, parents/$parentUid: { linkedAt, displayName, avatar } }
 *
 * Espace parents (Phase 2a) : parentSummaries/$uid = résumé non nominatif (hors prénom/avatar)
 * maintenu par le client de l'élève après chaque activité (voir updateParentSummary).
 */
import { ref, get, update, onValue, push, set, off } from "firebase/database";
import { subDays } from "date-fns";
import { db } from "../firebase";
import type { Catalog, Exercise, ExerciseLevel, FullChapter, TheoryLevel, Year } from "../content/types";
import { dayKey, daysBetween } from "../utils/dates";
import { getCatalog } from "./contentService";
import { track } from "./analytics";

export type ProfileRole = "student" | "parent";

export interface StudentProfile {
  displayName: string;
  /** Absent en base pour un parent (role === "parent") : les écrans élèves ne sont jamais rendus pour lui. */
  year: Year;
  /** Idem : sans objet pour un parent. */
  dailyGoalMinutes: number;
  createdAt: number;
  avatar?: string; // emoji
  /** Absent = "student". */
  role?: ProfileRole;
  /** Rappels locaux (natif uniquement, Phase 2b). Absent = désactivé. */
  notifications?: NotificationPrefs;
  /** Façon d'apprendre (Phase 3b, élèves seulement). Absent = questionnaire jamais proposé. */
  learningProfile?: LearningProfile;
}

/**
 * Profil d'apprentissage (docs/PROF-IA-VIDEO.md §1) : détermine la manière dont le prof IA
 * explique (cours animé, chat, explication d'erreur). Transmis tel quel au proxy-server.
 */
export interface LearningProfile {
  version: 1;
  /** Comment aborder une notion : par des exemples concrets d'abord, ou par la règle d'abord */
  approach: "examples" | "rule";
  /** Rythme souhaité */
  pace: "slow" | "normal" | "fast";
  /** Niveau de détail préféré */
  detail: "short" | "detailed";
  /** Aime les schémas / tableaux / visuels */
  visual: boolean;
  /** Aime les analogies de la vie quotidienne (argent de poche, sport, cuisine, jeux) */
  analogies: boolean;
  /** Préfère lire, écouter, ou les deux */
  channel: "read" | "listen" | "both";
  /** Confiance par matière, 1 (difficile) → 3 (à l'aise) */
  confidence: { maths: 1 | 2 | 3; francais: 1 | 2 | 3; allemand: 1 | 2 | 3; anglais: 1 | 2 | 3 };
  /** Motivation principale */
  goal: "understand" | "prepareTest" | "getAhead";
  /** 0 = questionnaire ignoré (« Plus tard ») : les défauts s'appliquent sans re-proposer le questionnaire. */
  completedAt: number;
}

export const DEFAULT_LEARNING_PROFILE: LearningProfile = {
  version: 1,
  approach: "examples",
  pace: "normal",
  detail: "short",
  visual: true,
  analogies: true,
  channel: "both",
  confidence: { maths: 2, francais: 2, allemand: 2, anglais: 2 },
  goal: "understand",
  completedAt: 0,
};

export interface NotificationPrefs {
  enabled: boolean;
  /** Heure locale du rappel quotidien (0-23). */
  hour: number;
  minute: number;
}

export function isParentProfile(p: StudentProfile | null | undefined): boolean {
  return p?.role === "parent";
}

export interface Streak {
  current: number;
  best: number;
  lastActiveDay: string | null;
}

export interface StudentStats {
  xp: number;
  level: number;
  streak: Streak;
  totalExercises: number;
  totalCorrect: number;
  totalTests: number;
  chaptersMastered: number;
}

export interface ExerciseProgress {
  attempts: number;
  correct: boolean;
  lastAt: number;
}

export interface TestResult {
  id?: string;
  at: number;
  score: number; // 0-100
  passed: boolean;
  correctIds: string[];
  wrongIds: string[];
  masteredLevels: ExerciseLevel[];
  toReviewLevels: ExerciseLevel[];
}

/**
 * Révision espacée (PDF §1, §13) : planifiée dès que le chapitre est maîtrisé (test réussi)
 * ou que tous ses exercices sont réussis. Intervalles 2 → 7 → 21 → 60 jours après chaque
 * révision réussie ; une révision ratée (< REVIEW_PASS_SCORE) repart à 2 jours.
 */
export interface ReviewState {
  /** Nombre de révisions réussies d'affilée (0 = jamais révisé ou dernière révision ratée). */
  count: number;
  /** Dernière planification ou révision (ms). */
  lastAt: number;
  /** Prochaine révision due (ms). */
  nextDueAt: number;
}

export interface ChapterProgress {
  theory?: Partial<Record<TheoryLevel, number>>;
  exercises?: Record<string, ExerciseProgress>;
  tests?: Record<string, TestResult>;
  bestTestScore?: number;
  completion: number;
  lastActivityAt?: number;
  review?: ReviewState;
}

export const REVIEW_INTERVALS_DAYS = [2, 7, 21, 60];
export const REVIEW_PASS_SCORE = 60;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Intervalle (jours) avant la prochaine révision selon le nombre de révisions réussies d'affilée. */
export function reviewIntervalDays(count: number): number {
  return REVIEW_INTERVALS_DAYS[Math.min(Math.max(0, count), REVIEW_INTERVALS_DAYS.length - 1)];
}

/** Première planification (2 jours). Ne touche pas à une révision déjà planifiée. */
export function scheduleFirstReview(p: ChapterProgress, now = Date.now()): ChapterProgress {
  if (p.review) return p;
  return { ...p, review: { count: 0, lastAt: now, nextDueAt: now + reviewIntervalDays(0) * DAY_MS } };
}

/** Après une révision : réussie → intervalle suivant (plafond 60 j), ratée → retour à 2 jours. */
export function nextReviewState(prev: ReviewState | undefined, passed: boolean, now = Date.now()): ReviewState {
  const count = passed ? (prev?.count || 0) + 1 : 0;
  return { count, lastAt: now, nextDueAt: now + reviewIntervalDays(count) * DAY_MS };
}

export type ProgressMap = Record<string, ChapterProgress>;

export const DEFAULT_STATS: StudentStats = {
  xp: 0,
  level: 1,
  streak: { current: 0, best: 0, lastActiveDay: null },
  totalExercises: 0,
  totalCorrect: 0,
  totalTests: 0,
  chaptersMastered: 0,
};

// ---------------------------------------------------------------------------
// XP / niveaux
// ---------------------------------------------------------------------------
export const XP = {
  theoryRead: 5,
  exerciseCorrect: (level: ExerciseLevel) => 10 * level,
  exerciseRetryCorrect: 5,
  testCompleted: 30,
  testPassed: 50,
  streakDay: 10,
  reviewPassed: 15,
  reviewFailed: 5,
};

/** Niveau = 1 + partie entière de sqrt(xp / 100) : 100, 400, 900, 1600 XP... */
export function levelForXp(xp: number): number {
  return 1 + Math.floor(Math.sqrt(Math.max(0, xp) / 100));
}
export function xpForLevel(level: number): number {
  return Math.pow(level - 1, 2) * 100;
}

// ---------------------------------------------------------------------------
// Lectures / abonnements
// ---------------------------------------------------------------------------
export function subscribeProfile(uid: string, cb: (p: StudentProfile | null) => void): () => void {
  const r = ref(db, `users/${uid}/profile`);
  const handler = onValue(r, (snap) => cb(snap.exists() ? (snap.val() as StudentProfile) : null));
  return () => off(r, "value", handler);
}

export function subscribeStats(uid: string, cb: (s: StudentStats) => void): () => void {
  const r = ref(db, `users/${uid}/stats`);
  const handler = onValue(r, (snap) => cb({ ...DEFAULT_STATS, ...(snap.val() || {}) }));
  return () => off(r, "value", handler);
}

export function subscribeProgress(uid: string, cb: (p: ProgressMap) => void): () => void {
  const r = ref(db, `users/${uid}/progress`);
  const handler = onValue(r, (snap) => cb((snap.val() as ProgressMap) || {}));
  return () => off(r, "value", handler);
}

export async function saveProfile(uid: string, profile: Partial<StudentProfile>): Promise<void> {
  await update(ref(db, `users/${uid}/profile`), profile);
}

/** Enregistre (ou remplace) le profil d'apprentissage d'un élève. */
export async function saveLearningProfile(uid: string, profile: LearningProfile): Promise<void> {
  await update(ref(db, `users/${uid}/profile`), { learningProfile: profile });
}

export async function createProfile(uid: string, profile: Omit<StudentProfile, "createdAt">): Promise<void> {
  const existing = await get(ref(db, `users/${uid}/profile`));
  const createdAt = existing.exists() && existing.val().createdAt ? existing.val().createdAt : Date.now();
  await update(ref(db, `users/${uid}`), {
    profile: { ...profile, createdAt },
    "publicProfile/displayName": profile.displayName,
  });
}

/** Profil parent : prénom + avatar uniquement (pas d'année scolaire ni d'objectif). */
export async function createParentProfile(uid: string, profile: { displayName: string; avatar?: string }): Promise<void> {
  const existing = await get(ref(db, `users/${uid}/profile`));
  const createdAt = existing.exists() && existing.val().createdAt ? existing.val().createdAt : Date.now();
  await update(ref(db, `users/${uid}`), {
    profile: { displayName: profile.displayName, avatar: profile.avatar || null, role: "parent", createdAt },
    "publicProfile/displayName": profile.displayName,
  });
}

// ---------------------------------------------------------------------------
// Calcul de complétion d'un chapitre (0-100)
//   théorie lue (n'importe quel niveau) : 20 · exercices réussis : 50 · test réussi : 30
// ---------------------------------------------------------------------------
export function computeCompletion(chapter: FullChapter, p: ChapterProgress | undefined): number {
  if (!p) return 0;
  const theoryDone = p.theory && Object.keys(p.theory).length > 0 ? 20 : 0;
  const total = chapter.exercises.length || 1;
  const correct = chapter.exercises.filter((e) => p.exercises?.[e.id]?.correct).length;
  const exPart = Math.round((correct / total) * 50);
  const testPart = (p.bestTestScore ?? 0) >= chapter.test.passScore ? 30 : 0;
  return Math.min(100, theoryDone + exPart + testPart);
}

/** Niveau d'exercice débloqué : niveau N+1 accessible quand 2 exercices du niveau N sont réussis
 *  (ou tous s'il y en a moins de 2). */
export function unlockedLevel(chapter: FullChapter, p: ChapterProgress | undefined): ExerciseLevel {
  let level: ExerciseLevel = 1;
  for (const l of [1, 2, 3] as ExerciseLevel[]) {
    const ofLevel = chapter.exercises.filter((e) => e.level === l);
    const done = ofLevel.filter((e) => p?.exercises?.[e.id]?.correct).length;
    const needed = Math.min(2, ofLevel.length);
    if (ofLevel.length > 0 && done >= needed) level = (l + 1) as ExerciseLevel;
    else break;
  }
  return level;
}

// ---------------------------------------------------------------------------
// Écritures : streak / xp / activité (helper partagé, aussi utilisé par reviewService)
// ---------------------------------------------------------------------------
export async function applyActivity(
  uid: string,
  xpGain: number,
  extra: { exercises?: number; correct?: number; tests?: number } = {}
): Promise<{ streakExtended: boolean; levelUp: boolean; stats: StudentStats }> {
  const statsRef = ref(db, `users/${uid}/stats`);
  const snap = await get(statsRef);
  const stats: StudentStats = { ...DEFAULT_STATS, ...(snap.val() || {}) };
  const today = dayKey();
  let streakExtended = false;
  let bonus = 0;

  const streak: Streak = { ...DEFAULT_STATS.streak, ...(stats.streak || {}) };
  if (streak.lastActiveDay !== today) {
    const gap = streak.lastActiveDay ? daysBetween(streak.lastActiveDay, today) : Infinity;
    streak.current = gap === 1 ? streak.current + 1 : 1;
    streak.best = Math.max(streak.best, streak.current);
    streak.lastActiveDay = today;
    streakExtended = true;
    bonus = XP.streakDay;
    track("streak_extended", { days: streak.current });
  }

  const newXp = stats.xp + xpGain + bonus;
  const newLevel = levelForXp(newXp);
  const levelUp = newLevel > (stats.level || 1);

  const next: StudentStats = {
    ...stats,
    xp: newXp,
    level: newLevel,
    streak,
    totalExercises: stats.totalExercises + (extra.exercises || 0),
    totalCorrect: stats.totalCorrect + (extra.correct || 0),
    totalTests: stats.totalTests + (extra.tests || 0),
  };

  const actRef = ref(db, `users/${uid}/activity/${today}`);
  const actSnap = await get(actRef);
  const act = actSnap.val() || { xp: 0, exercises: 0, seconds: 0 };

  await update(ref(db, `users/${uid}`), {
    stats: next,
    [`activity/${today}`]: {
      ...act,
      xp: (act.xp || 0) + xpGain + bonus,
      exercises: (act.exercises || 0) + (extra.exercises || 0),
    },
  });
  return { streakExtended, levelUp, stats: next };
}

/** Ajoute du temps d'activité (appelé par le hook useStudyTimer, toutes les 30 s). */
export async function addStudySeconds(uid: string, seconds: number): Promise<void> {
  const today = dayKey();
  const actRef = ref(db, `users/${uid}/activity/${today}/seconds`);
  const snap = await get(actRef);
  await set(actRef, (snap.val() || 0) + seconds);
  // Résumé parent rafraîchi au plus toutes les 2 min (le minuteur écrit toutes les 30 s).
  refreshParentSummary(uid, 2 * 60 * 1000);
}

// ---------------------------------------------------------------------------
// Événements pédagogiques
// ---------------------------------------------------------------------------
export async function markTheoryRead(uid: string, chapter: FullChapter, level: TheoryLevel): Promise<void> {
  const pRef = ref(db, `users/${uid}/progress/${chapter.id}`);
  const snap = await get(pRef);
  const p: ChapterProgress = snap.val() || { completion: 0 };
  const already = Boolean(p.theory?.[level]);
  const theory = { ...(p.theory || {}), [level]: Date.now() };
  const next = { ...p, theory, lastActivityAt: Date.now() };
  next.completion = computeCompletion(chapter, next);
  await set(pRef, next);
  track("theory_viewed", { level });
  if (!already) await applyActivity(uid, XP.theoryRead);
  refreshParentSummary(uid);
}

export interface AttemptOutcome {
  xpGained: number;
  levelUp: boolean;
  streakExtended: boolean;
  newExerciseLevelUnlocked: ExerciseLevel | null;
}

export async function recordExerciseAttempt(
  uid: string,
  chapter: FullChapter,
  exercise: Exercise,
  correct: boolean
): Promise<AttemptOutcome> {
  const pRef = ref(db, `users/${uid}/progress/${chapter.id}`);
  const snap = await get(pRef);
  const p: ChapterProgress = snap.val() || { completion: 0 };
  const prev = p.exercises?.[exercise.id] || { attempts: 0, correct: false, lastAt: 0 };
  const before = unlockedLevel(chapter, p);
  const exercises = {
    ...(p.exercises || {}),
    [exercise.id]: { attempts: prev.attempts + 1, correct: prev.correct || correct, lastAt: Date.now() },
  };
  let next: ChapterProgress = { ...p, exercises, lastActivityAt: Date.now() };
  next.completion = computeCompletion(chapter, next);
  if (chapter.exercises.every((e) => next.exercises?.[e.id]?.correct)) next = scheduleFirstReview(next);
  await set(pRef, next);
  track("exercise_answered", { level: exercise.level, correct });

  let xpGained = 0;
  if (correct) xpGained = prev.correct ? XP.exerciseRetryCorrect : XP.exerciseCorrect(exercise.level);
  const res = await applyActivity(uid, xpGained, { exercises: 1, correct: correct ? 1 : 0 });
  const after = unlockedLevel(chapter, next);
  if (after > before) track("level_unlocked", { level: after });
  refreshParentSummary(uid);
  return {
    xpGained,
    levelUp: res.levelUp,
    streakExtended: res.streakExtended,
    newExerciseLevelUnlocked: after > before ? after : null,
  };
}

export async function recordTest(
  uid: string,
  chapter: FullChapter,
  result: Omit<TestResult, "id">
): Promise<{ id: string; xpGained: number }> {
  const pRef = ref(db, `users/${uid}/progress/${chapter.id}`);
  const snap = await get(pRef);
  const p: ChapterProgress = snap.val() || { completion: 0 };
  const testRef = push(ref(db, `users/${uid}/progress/${chapter.id}/tests`));
  const id = testRef.key as string;
  const wasMastered = (p.bestTestScore ?? 0) >= chapter.test.passScore;
  const tests = { ...(p.tests || {}), [id]: { ...result, id } };
  const bestTestScore = Math.max(p.bestTestScore ?? 0, result.score);
  const nowMastered = bestTestScore >= chapter.test.passScore;
  let next: ChapterProgress = { ...p, tests, bestTestScore, lastActivityAt: Date.now() };
  next.completion = computeCompletion(chapter, next);
  if (nowMastered) next = scheduleFirstReview(next);
  await set(pRef, next);
  track("test_completed", { score: result.score, passed: result.passed });

  const xpGained = XP.testCompleted + (result.passed ? XP.testPassed : 0);
  const res = await applyActivity(uid, xpGained, { tests: 1 });
  if (nowMastered && !wasMastered) {
    await update(ref(db, `users/${uid}/stats`), { chaptersMastered: (res.stats.chaptersMastered || 0) + 1 });
  }
  refreshParentSummary(uid);
  return { id, xpGained };
}

// ---------------------------------------------------------------------------
// Chat : historique par chapitre (30 derniers messages conservés)
// ---------------------------------------------------------------------------
export interface StoredChatMessage {
  role: "user" | "assistant";
  content: string;
  at: number;
}

export function subscribeChat(uid: string, chapterId: string, cb: (m: StoredChatMessage[]) => void): () => void {
  const r = ref(db, `users/${uid}/chats/${chapterId}/messages`);
  const handler = onValue(r, (snap) => {
    const v = (snap.val() || {}) as Record<string, StoredChatMessage>;
    cb(Object.values(v).sort((a, b) => a.at - b.at));
  });
  return () => off(r, "value", handler);
}

export async function appendChatMessage(uid: string, chapterId: string, m: StoredChatMessage): Promise<void> {
  await set(push(ref(db, `users/${uid}/chats/${chapterId}/messages`)), m);
}

export async function clearChat(uid: string, chapterId: string): Promise<void> {
  await set(ref(db, `users/${uid}/chats/${chapterId}`), null);
}

// ---------------------------------------------------------------------------
// Activité (tableau de bord / espace parents)
// ---------------------------------------------------------------------------
export interface DayActivity {
  xp: number;
  exercises: number;
  seconds: number;
}
export function subscribeActivity(uid: string, cb: (a: Record<string, DayActivity>) => void): () => void {
  const r = ref(db, `users/${uid}/activity`);
  const handler = onValue(r, (snap) => cb((snap.val() as Record<string, DayActivity>) || {}));
  return () => off(r, "value", handler);
}

// ---------------------------------------------------------------------------
// Espace parents : résumé non nominatif (parentSummaries/$uid), écrit par l'élève,
// lu par les parents liés (règles RTDB). Jamais le contenu des discussions IA.
// ---------------------------------------------------------------------------
export interface ParentSummaryTest {
  chapterTitle: string;
  score: number;
  passed: boolean;
  at: number;
}

export interface ParentSummary {
  displayName: string;
  avatar?: string;
  year?: Year;
  updatedAt: number;
  streak: number;
  xp: number;
  level: number;
  todayMinutes: number;
  /** 7 derniers jours, aujourd'hui compris */
  weekMinutes: number;
  chaptersStarted: number;
  chaptersMastered: number;
  lastChapterTitle?: string;
  /** Titres de chapitres commencés non maîtrisés (max 3) */
  toReview?: string[];
  /** Derniers tests (max 5, du plus récent au plus ancien) */
  recentTests?: ParentSummaryTest[];
}

export function buildParentSummary(
  catalog: Catalog,
  profile: StudentProfile,
  stats: StudentStats,
  progress: ProgressMap,
  activity: Record<string, DayActivity>
): ParentSummary {
  const days = Array.from({ length: 7 }, (_, i) => dayKey(subDays(new Date(), i)));
  const minutes = (d: string) => Math.round((activity[d]?.seconds || 0) / 60);
  const started = catalog.chapters.filter((c) => progress[c.id]);
  const byRecent = [...started].sort((a, b) => (progress[b.id].lastActivityAt || 0) - (progress[a.id].lastActivityAt || 0));
  const toReview = started
    .filter((c) => (progress[c.id].completion || 0) < 100)
    .sort((a, b) => (progress[a.id].lastActivityAt || 0) - (progress[b.id].lastActivityAt || 0))
    .slice(0, 3)
    .map((c) => c.title);
  const recentTests: ParentSummaryTest[] = [];
  for (const c of started) {
    for (const tst of Object.values(progress[c.id].tests || {})) {
      recentTests.push({ chapterTitle: c.title, score: tst.score, passed: tst.passed, at: tst.at });
    }
  }
  recentTests.sort((a, b) => b.at - a.at);
  return {
    displayName: profile.displayName,
    avatar: profile.avatar,
    year: profile.year,
    updatedAt: Date.now(),
    streak: stats.streak?.current || 0,
    xp: stats.xp || 0,
    level: stats.level || 1,
    todayMinutes: minutes(days[0]),
    weekMinutes: days.reduce((acc, d) => acc + minutes(d), 0),
    chaptersStarted: started.length,
    chaptersMastered: stats.chaptersMastered || 0,
    lastChapterTitle: byRecent[0]?.title,
    toReview,
    recentTests: recentTests.slice(0, 5),
  };
}

/**
 * Recalcule et écrit parentSummaries/$uid à partir des données de l'élève.
 * Tolère l'échec (règles, réseau) : ne doit jamais bloquer l'activité pédagogique.
 */
export async function updateParentSummary(uid: string, catalog?: Catalog): Promise<void> {
  const cat = catalog || (await getCatalog());
  const [profileSnap, statsSnap, progressSnap, activitySnap] = await Promise.all([
    get(ref(db, `users/${uid}/profile`)),
    get(ref(db, `users/${uid}/stats`)),
    get(ref(db, `users/${uid}/progress`)),
    get(ref(db, `users/${uid}/activity`)),
  ]);
  const profile = profileSnap.val() as StudentProfile | null;
  if (!profile || isParentProfile(profile)) return;
  const stats: StudentStats = { ...DEFAULT_STATS, ...(statsSnap.val() || {}) };
  const summary = buildParentSummary(cat, profile, stats, (progressSnap.val() as ProgressMap) || {}, activitySnap.val() || {});
  // Les champs undefined sont refusés par la RTDB : on les retire.
  const clean = JSON.parse(JSON.stringify(summary));
  await set(ref(db, `parentSummaries/${uid}`), clean);
}

let lastSummaryAt: Record<string, number> = {};
/** Version « et on n'attend pas » avec intervalle minimal optionnel (ms). */
export function refreshParentSummary(uid: string, minIntervalMs = 0): void {
  const now = Date.now();
  if (minIntervalMs > 0 && now - (lastSummaryAt[uid] || 0) < minIntervalMs) return;
  lastSummaryAt = { [uid]: now };
  updateParentSummary(uid).catch(() => undefined);
}

export function subscribeParentSummary(studentUid: string, cb: (s: ParentSummary | null) => void): () => void {
  const r = ref(db, `parentSummaries/${studentUid}`);
  const handler = onValue(
    r,
    (snap) => cb(snap.exists() ? (snap.val() as ParentSummary) : null),
    () => cb(null)
  );
  return () => off(r, "value", handler);
}
